/** LEAVE-APPROVALS-UI-002 — tabs, compact filter bar, pagination + per-tab state (presentation only). */
import React, { useMemo, useState } from 'react';
import { Search, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import './leave-approvals.css';

export const PAGE_SIZES = [10, 25, 50];
export const DEFAULT_TAB_STATE = { page: 1, pageSize: 10, search: '', type: 'all', date: 'all', from: '', to: '', status: 'all' };

export const useTabStates = (keys) => {
  const [states, setStates] = useState(() => Object.fromEntries(keys.map(k => [k, { ...DEFAULT_TAB_STATE }])));
  const update = (key, patch, resetPage = true) =>
    setStates(prev => ({ ...prev, [key]: { ...prev[key], ...patch, ...(resetPage ? { page: 1 } : {}) } }));
  return [states, update];
};

export const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';
export const Avatar = ({ name, tone }) => <span className="la-avatar" data-tone={tone}>{initials(name)}</span>;

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export const dateWindow = (preset, from, to) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  switch (preset) {
    case 'today': return [iso(today), iso(today)];
    case 'week': { const s = addDays(today, -((today.getDay() + 6) % 7)); return [iso(s), iso(addDays(s, 6))]; }
    case 'month': return [iso(new Date(today.getFullYear(), today.getMonth(), 1)), iso(new Date(today.getFullYear(), today.getMonth() + 1, 0))];
    case 'next30': return [iso(today), iso(addDays(today, 30))];
    case 'past30': return [iso(addDays(today, -30)), iso(today)];
    case 'custom': return [from || null, to || null];
    default: return null;
  }
};

/** Filters a dataset for one tab. `getDates(r)` → [start, end]; `getStatus(r)`; `getText(r)` → searchable string. */
export const applyFilters = (rows, st, { getDates, getStatus, getText }) => {
  const q = st.search.trim().toLowerCase();
  const win = dateWindow(st.date, st.from, st.to);
  return rows.filter(r => {
    if (q && !getText(r).toLowerCase().includes(q)) return false;
    if (st.type !== 'all' && r.leave_type !== st.type) return false;
    if (st.status !== 'all' && getStatus && getStatus(r) !== st.status) return false;
    if (win) {
      const [s, e] = getDates(r); const [ws, we] = win;
      if (ws && e < ws) return false;
      if (we && s > we) return false;
    }
    return true;
  });
};

export const paginate = (rows, st) => {
  const totalPages = Math.max(1, Math.ceil(rows.length / st.pageSize));
  const page = Math.min(st.page, totalPages);
  return { page, totalPages, slice: rows.slice((page - 1) * st.pageSize, page * st.pageSize) };
};

export const ApprovalTabs = ({ tabs, active, onChange }) => (
  <div className="la-tabs" role="tablist" aria-label="Leave approval categories" data-testid="approval-tabs">
    {tabs.map(t => (
      <button key={t.key} type="button" role="tab" aria-selected={active === t.key} aria-controls={`la-panel-${t.key}`} id={`la-tab-${t.key}`}
        className="la-tab" data-tone={t.tone} data-testid={`approval-tab-${t.key}`} onClick={() => onChange(t.key)}>
        <t.icon className="w-4 h-4" style={{ color: active === t.key ? 'var(--la-c)' : undefined }} />
        <span>{t.label}</span>
        <span className="la-count" data-testid={`approval-tab-count-${t.key}`}>{t.count}</span>
      </button>
    ))}
  </div>
);

export const FilterBar = ({ st, onChange, onReset, types, statuses, tab, searchPlaceholder }) => (
  <div className="la-filters" data-testid={`approval-filters-${tab}`}>
    <label className="la-search">
      <Search className="w-4 h-4 text-slate-400" />
      <input value={st.search} onChange={e => onChange({ search: e.target.value })} placeholder={searchPlaceholder} data-testid={`approval-search-${tab}`} />
    </label>
    {types && (
      <select className="la-select" value={st.type} onChange={e => onChange({ type: e.target.value })} data-testid={`approval-type-filter-${tab}`} aria-label="Leave type">
        <option value="all">All Leave Types</option>
        {types.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    )}
    <select className="la-select" value={st.date} onChange={e => onChange({ date: e.target.value })} data-testid={`approval-date-filter-${tab}`} aria-label="Dates">
      <option value="all">All Dates</option>
      <option value="today">Today</option>
      <option value="week">This Week</option>
      <option value="month">This Month</option>
      <option value="next30">Next 30 Days</option>
      <option value="past30">Past 30 Days</option>
      <option value="custom">Custom Range…</option>
    </select>
    {st.date === 'custom' && (
      <span className="flex items-center gap-1.5 la-panel" data-testid={`approval-date-range-${tab}`}>
        <Calendar className="w-4 h-4 text-slate-400" />
        <input type="date" className="la-date" value={st.from} onChange={e => onChange({ from: e.target.value })} data-testid={`approval-date-from-${tab}`} aria-label="From date" />
        <span className="text-slate-400 text-xs">to</span>
        <input type="date" className="la-date" value={st.to} onChange={e => onChange({ to: e.target.value })} data-testid={`approval-date-to-${tab}`} aria-label="To date" />
      </span>
    )}
    {statuses && (
      <select className="la-select" value={st.status} onChange={e => onChange({ status: e.target.value })} data-testid={`approval-status-filter-${tab}`} aria-label="Status">
        <option value="all">All Status</option>
        {statuses.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    )}
    <button type="button" className="la-reset" onClick={onReset} data-testid={`approval-reset-${tab}`}>Reset</button>
  </div>
);

const pageList = (page, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const s = new Set([1, total, page - 1, page, page + 1].filter(p => p >= 1 && p <= total));
  const out = []; let prev = 0;
  [...s].sort((a, b) => a - b).forEach(p => { if (p - prev > 1) out.push('…'); out.push(p); prev = p; });
  return out;
};

export const PaginationFooter = ({ st, total, page, totalPages, onChange, tab, noun = 'requests' }) => {
  const from = total === 0 ? 0 : (page - 1) * st.pageSize + 1;
  const to = Math.min(page * st.pageSize, total);
  const pages = useMemo(() => pageList(page, totalPages), [page, totalPages]);
  return (
    <div className="la-pager" data-testid={`approval-pagination-${tab}`}>
      <label className="flex items-center gap-2">
        Show
        <select className="la-select" style={{ height: 34 }} value={st.pageSize} onChange={e => onChange({ pageSize: Number(e.target.value) })} data-testid={`approval-page-size-${tab}`} aria-label="Entries per page">
          {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        entries
      </label>
      <span data-testid={`approval-range-${tab}`}>Showing {from}–{to} of {total} {total === 1 ? noun.replace(/s$/, '') : noun}</span>
      <nav className="flex items-center gap-1.5" aria-label="Pagination">
        <button type="button" className="la-page-btn" disabled={page <= 1} onClick={() => onChange({ page: page - 1 }, false)} data-testid={`approval-prev-${tab}`}><ChevronLeft className="w-4 h-4" /> Previous</button>
        {pages.map((p, i) => p === '…'
          ? <span key={`e${i}`} className="px-1 text-slate-400">…</span>
          : <button key={p} type="button" className="la-page-btn" aria-current={p === page ? 'page' : undefined} onClick={() => onChange({ page: p }, false)} data-testid={`approval-page-${tab}-${p}`}>{p}</button>)}
        <button type="button" className="la-page-btn" disabled={page >= totalPages} onClick={() => onChange({ page: page + 1 }, false)} data-testid={`approval-next-${tab}`}>Next <ChevronRight className="w-4 h-4" /></button>
      </nav>
    </div>
  );
};

export const TableSkeleton = ({ cols = 7, rows = 4 }) => (
  <tbody data-testid="approval-table-skeleton">
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={r}>{Array.from({ length: cols }).map((__, c) => <td key={c}><div className="la-shimmer h-3.5" style={{ width: c === 0 ? '70%' : '55%' }} /></td>)}</tr>
    ))}
  </tbody>
);
